import { Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { resolveDashboardRoute, pickPrimaryRole, Role } from "@/lib/roles";

/**
 * 🎯 Dashboard Dispatcher (centralisé)
 *
 * Point d'entrée unique pour toute redirection vers un dashboard.
 * - URL publique : /dashboard
 * - Vérifie l'authentification
 * - Lit les rôles Firebase et redirige via ROLE_DASHBOARD_MAP (source unique : src/lib/roles.ts)
 * - Évite le clignotement (loader pendant la résolution)
 * - Gère le cas "aucun rôle" → message clair
 *
 * Pour ajouter un rôle : modifier UNIQUEMENT src/lib/roles.ts (ROLE_DASHBOARD_MAP + ROLE_PRIORITY).
 */
export default function Dashboard() {
  const { user, loading } = useAuth();

  // 1. Auth en cours → loader
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary" />
      </div>
    );
  }

  // 2. Non connecté → login (on mémorise la destination)
  if (!user) {
    sessionStorage.setItem("intended_destination", "/dashboard");
    return <Navigate to="/login" replace />;
  }

  // 3. Connecté mais sans rôle → message clair (pas de boucle)
  const roles = ((user as any).roles as Role[] | undefined) ?? [];
  if (roles.length === 0) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-6 bg-background">
        <div className="max-w-md text-center space-y-4">
          <h1 className="text-2xl font-bold text-foreground">
            Compte en attente d'activation
          </h1>
          <p className="text-muted-foreground">
            Aucun rôle n'est encore associé à votre compte. Contactez le support
            pour finaliser votre accès.
          </p>
          <a
            href="/contact"
            className="inline-block px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90"
          >
            Contacter le support
          </a>
        </div>
      </div>
    );
  }

  // 4. Résolution centralisée → redirection vers le bon dashboard
  const target = resolveDashboardRoute(pickPrimaryRole(roles) as Role);
  return <Navigate to={target} replace />;
}
